import { inject, Type } from '@angular/core';
import { FocusStateService } from 'services/focus-state.service';
import { ResumeRepaintService } from 'services/resume-repaint.service';
import { SafeAreaOverlapMonitorService } from 'services/safe-area-overlap-monitor.service';

/**
 * The services that exist ONLY to be built.
 *
 * Each one registers browser event listeners in its own constructor and is never
 * called into afterwards, so constructing it is what switches it on. Angular builds
 * a `providedIn: 'root'` service on first injection and never before, which means a
 * service nobody injects simply does not exist — with no error anywhere.
 *
 * Add a service here to switch it on; that is the whole job. REMOVING an entry
 * switches its feature off, silently, so do that only when the service itself is
 * going away.
 *
 * Why this list exists rather than a parameter on `MyApp`'s constructor, and what
 * the order below is for: `design-docs/boot-sequence.md`, "Where the resume repaint
 * gets switched on".
 */
const APP_WIDE_LISTENERS: readonly Type<unknown>[] = [
  // registers `focus`, `blur` and `visibilitychange` listeners and publishes
  // focusStateChangedEvent when the app goes to and from the foreground.
  FocusStateService,
  // #4710: listens for the page going off screen, and redraws the page the user is
  // looking at when it comes back. Without this the app can return from the
  // background showing nothing at all.
  ResumeRepaintService,
  // #5810: on the native apps, reports to Sentry any tappable control stuck under the system
  // navigation bar. Does nothing on the web.
  SafeAreaOverlapMonitorService,
];

/**
 * Build every service in the list above, for its side effects.
 *
 * Must be called from an Angular injection context — `MyApp`'s constructor is the
 * one caller. `inject()` throws loudly outside one, so a call in the wrong place
 * fails at boot rather than quietly registering nothing.
 */
export function buildAppWideListeners(): void {
  // for each service that exists only to be built, build it
  for (const service of APP_WIDE_LISTENERS) {
    inject(service);
  }
}
